import React from "react";
import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import FontIcon, { ODL_ICONS } from "@odl/core/components/DataDisplay/FontIcon";
import { css } from "@emotion/react";
import Guard from "components/Guard/Guard";

type Props = {
  icon: string;
  colour: string;
  iconColour: string;
  size?: number;
  iconSize?: number;
};

const ItemStatusIcon: React.FC<Props> = ({ icon, colour, iconColour, size, iconSize = 24 }) => {
  const isODLIcons = React.useMemo(() => {
    return Object.values<string>(ODL_ICONS).includes(icon);
  }, [icon]);

  return (
    <StyledStatusIconBackground $colour={colour} $size={size} $iconSize={iconSize}>
      <Guard
        condition={isODLIcons}
        fallback={
          <img
            data-testid={"ImgIcon"}
            data-icon={icon}
            src={icon}
            alt={"Status Icon"}
            width={iconSize}
            height={iconSize}
          />
        }
      >
        <StyledFontIcon
          name={icon as typeof ODL_ICONS[keyof typeof ODL_ICONS]}
          color={iconColour}
          $size={iconSize}
          data-testid="FontIcon"
          data-icon={icon}
        />
      </Guard>
    </StyledStatusIconBackground>
  );
};

const StyledFontIcon = styled(FontIcon, {
  shouldForwardProp: (prop) => prop !== "$size",
})<{ $size: number }>(
  ({ theme, $size }) => css`
    font-size: ${$size}px;
    width: ${$size}px;
    height: ${$size}px;
  `
);

const StyledStatusIconBackground = styled(Box, {
  shouldForwardProp: (prop) => !["$colour", "$size", "$iconSize"].includes(prop.toString()),
})<{ $colour: string; $size?: number; $iconSize: number }>(
  ({ theme, $colour, $size, $iconSize }) => css`
    width: calc(${$iconSize}px * 2);
    height: calc(${$iconSize}px * 2);
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    background: ${$colour};
    border-radius: 50%;

    ${$size && `width: ${$size}px; height: ${$size}px;`}
  `
);

export default ItemStatusIcon;
